import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import { api, photoUrl } from "../lib/api";

const VARIANT_PLACEMENT = { leaderboard: "home", leaderboard_top: "home_top", rectangle: "listing", strip: "browse", strip_bottom: "browse_bottom", sidebar: "browse_sidebar" };

// Serves a real self-served banner ad for its slot; falls back to a teaser when none is live.
export const BannerAd = ({ variant = "leaderboard", className = "" }) => {
  const sizes = {
    leaderboard: "h-24 sm:h-28",
    rectangle: "h-64",
    strip: "h-20",
    strip_bottom: "h-20",
    sidebar: "h-64",
  };
  const placement = VARIANT_PLACEMENT[variant] || "home";
  const isHome = variant === "leaderboard" || variant === "leaderboard_top";
  const hideWhenEmpty = variant === "leaderboard_top";
  const [ad, setAd] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/ads/serve?placement=${placement}`)
      .then((r) => { if (alive) setAd(r.data.ad); })
      .catch(() => {});
    return () => { alive = false; };
  }, [placement]);

  const onClick = () => {
    if (!ad) return;
    api.post(`/ads/${ad.id}/click`).catch(() => {});
    const url = ad.link_url;
    if (url.startsWith("/")) window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  const banner = ad ? (
    <button
      type="button"
      onClick={onClick}
      data-testid={`banner-ad-${variant}`}
      aria-label={ad.title || "Advertisement"}
      className={
        isHome
          ? "relative flex items-center justify-center w-full max-w-[1200px] h-[120px] mx-auto bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 group"
          : `relative block w-full ${sizes[variant]} rounded-2xl overflow-hidden border border-slate-200 group`
      }
    >
      <img
        src={photoUrl(ad.image)}
        alt={ad.title || "Advertisement"}
        className={
          isHome
            ? "w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
            : "w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        }
      />
      <span className="absolute top-2 right-3 text-[10px] uppercase tracking-widest text-white/90 font-semibold bg-black/30 backdrop-blur-sm rounded px-1.5 py-0.5">Ad</span>
      {ad.title && (
        <span className="absolute bottom-0 left-0 right-0 text-left text-sm font-semibold text-white px-4 py-2 bg-gradient-to-t from-black/60 to-transparent">{ad.title}</span>
      )}
    </button>
  ) : (
    <div
      data-testid={`banner-ad-${variant}`}
      className={
        isHome
          ? "relative w-full max-w-[1200px] h-[120px] mx-auto rounded-2xl border border-dashed border-slate-300 bg-gradient-to-r from-slate-50 to-[#FFF4F0] flex flex-col items-center justify-center text-center overflow-hidden"
          : `relative w-full ${sizes[variant]} rounded-2xl border border-dashed border-slate-300 bg-gradient-to-r from-slate-50 to-[#FFF4F0] flex flex-col items-center justify-center text-center overflow-hidden`
      }
    >
      <span className="absolute top-2 right-3 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Advertisement</span>
      <Megaphone size={20} className="text-[#F5551E] mb-1" />
      <p className="text-sm font-semibold text-[#12234A]">Your ad could be here</p>
      <p className="text-xs text-slate-400">Reach thousands of Canadian pet lovers — advertise on CritterPosts.ca</p>
    </div>
  );

  if (!ad && hideWhenEmpty) return null;

  return (
    <div className={className}>
      {banner}
      <div className="text-center mt-1">
        <Link to="/advertising" data-testid={`banner-ad-info-link-${variant}`} className="text-[10px] text-slate-400 hover:text-[#F5551E] transition-colors">Advertise on CritterPosts.ca &rsaquo;</Link>
      </div>
    </div>
  );
};
